import { CasesStatistics } from "./CasesStatistics";
import { FirmDocumentFoldersStatistics } from "./FirmDocumentFoldersStatistics";
import { ProvidersStatistics } from "./ProvidersStatistics";
import { TemplateFilesStatistics } from "./TemplateFilesStatistics";
import { IntakesStatistics } from "./IntakesStatistics";

export interface MigrationStatistics {
  organizationId: string;
  allocatedSharepointSpaceInGb?: number;
  usedSharepointSpaceInGb?: number;
  casesStatistics: CasesStatistics;
  intakesStatistics: IntakesStatistics;
  providersStatistics: ProvidersStatistics;
  firmDocumentFoldersStatistics: FirmDocumentFoldersStatistics;
  templateFilesStatistics: TemplateFilesStatistics;
}

export interface FilesStatistics {
  TotalFilesSizeInGb: number;
  TotalFilesSizeInMb: number;
  MigratedFilesSizeInGb: number;
  MigratedFilesSizeInMb: number;
  MigratedPercentage: number;
  TotalFinishedFiles: number;
  TotalFailedInitialFiles: number;
  TotalFailedDeltaFiles: number;
  TotalModifiedFiles: number;
  TotalNotFoundFiles: number;
  TotalNotFoundMarkedForRetryFiles: number;
  TotalNotStartedFiles: number;
  TotalBlockedByConflictFiles: number;
  TotalAcknowledgedConflictFiles: number;
  TotalFlaggedFiles: number;
  TotalSkippedDuplicateFiles: number;
  NotFoundFilesSizeInGb: number;
  NotFoundFilesSizeInMb: number;
  InterruptedFilesSizeInGb: number;
  InterruptedFilesSizeInMb: number;
  SkippedDuplicateFilesSizeInGb: number;
  SkippedDuplicateFilesSizeInMb: number;
}

export const calculateTotalFileSize = (
  statistics: MigrationStatistics,
  displayOpenCaseAndIntakeStatistics: boolean,
  displayClosedCaseAndIntakeStatistics: boolean,
  inMb = false,
): number => {
  const {
    casesStatistics,
    intakesStatistics,
    providersStatistics,
    firmDocumentFoldersStatistics,
    templateFilesStatistics,
  } = statistics;
  if (inMb) {
    let totalSize =
      casesStatistics.totalCaseFolderFilesSizeInMb +
      providersStatistics.totalFilesSizeInMb +
      firmDocumentFoldersStatistics.totalFilesSizeInMb +
      templateFilesStatistics.totalFilesSizeInMb;

    if (displayOpenCaseAndIntakeStatistics)
    {
      totalSize += casesStatistics.totalOpenCaseFilesSizeInMb;
      totalSize += intakesStatistics.totalOpenIntakeFilesSizeInMb;
    }
    if (displayClosedCaseAndIntakeStatistics)
    {
      totalSize += casesStatistics.totalClosedCaseFilesSizeInMb;
      totalSize += intakesStatistics.totalClosedIntakeFilesSizeInMb;
    }
    
    return totalSize;
  } else {
    let totalSize =
      casesStatistics.totalCaseFolderFilesSizeInGb +
      providersStatistics.totalFilesSizeInGb +
      firmDocumentFoldersStatistics.totalFilesSizeInGb +
      templateFilesStatistics.totalFilesSizeInGb

    if (displayOpenCaseAndIntakeStatistics)
    {
      totalSize += casesStatistics.totalOpenCaseFilesSizeInGb;
      totalSize += intakesStatistics.totalOpenIntakeFilesSizeInGb;
    }
    if (displayClosedCaseAndIntakeStatistics)
    {
      totalSize += casesStatistics.totalClosedCaseFilesSizeInGb;
      totalSize += intakesStatistics.totalClosedIntakeFilesSizeInGb;
    }

    return totalSize;
  }
};

export const calculateMigratedFilesSize = (
  statistics: MigrationStatistics,
  displayOpenCaseAndIntakeStatistics: boolean,
  displayClosedCaseAndIntakeStatistics: boolean,
  inMb = false,
): number => {
  let migratedFilesSize = 0;
  if (inMb) {
    migratedFilesSize += statistics.casesStatistics.totalMigratedCaseFolderFilesSizeInMb || 0;
    migratedFilesSize += statistics.providersStatistics.totalMigratedFilesSizeInMb || 0;
    migratedFilesSize += statistics.firmDocumentFoldersStatistics.totalMigratedFilesSizeInMb || 0;
    migratedFilesSize += statistics.templateFilesStatistics.totalMigratedFilesSizeInMb || 0;

    if (displayOpenCaseAndIntakeStatistics)
    {
      migratedFilesSize += statistics.casesStatistics.totalMigratedOpenCaseFilesSizeInMb || 0;
      migratedFilesSize += statistics.intakesStatistics.totalMigratedOpenIntakeFilesSizeInMb || 0;
    }
    if (displayClosedCaseAndIntakeStatistics)
    {
      migratedFilesSize += statistics.casesStatistics.totalMigratedClosedCaseFilesSizeInMb || 0;
      migratedFilesSize += statistics.intakesStatistics.totalMigratedClosedIntakeFilesSizeInMb || 0;
    }
  } else {
    migratedFilesSize += statistics.casesStatistics.totalMigratedCaseFolderFilesSizeInGb || 0;
    migratedFilesSize += statistics.providersStatistics.totalMigratedFilesSizeInGb || 0;
    migratedFilesSize += statistics.firmDocumentFoldersStatistics.totalMigratedFilesSizeInGb || 0;
    migratedFilesSize += statistics.templateFilesStatistics.totalMigratedFilesSizeInGb || 0;

    if (displayOpenCaseAndIntakeStatistics)
    {
      migratedFilesSize += statistics.casesStatistics.totalMigratedOpenCaseFilesSizeInGb || 0;
      migratedFilesSize += statistics.intakesStatistics.totalMigratedOpenIntakeFilesSizeInGb || 0;
    }
    if (displayClosedCaseAndIntakeStatistics)
    {
      migratedFilesSize += statistics.casesStatistics.totalMigratedClosedCaseFilesSizeInGb || 0;
      migratedFilesSize += statistics.intakesStatistics.totalMigratedClosedIntakeFilesSizeInGb || 0;
    }
  }
  return migratedFilesSize;
};

export const calculateFilesStatistics = (
  statistics: MigrationStatistics,
  displayOpenCaseAndIntakeStatistics: boolean,
  displayClosedCaseAndIntakeStatistics: boolean
): FilesStatistics => {
  const {
    casesStatistics,
    intakesStatistics,
    providersStatistics,
    firmDocumentFoldersStatistics,
    templateFilesStatistics,
  } = statistics;
  
  const totalFileSizeInGb = calculateTotalFileSize(statistics, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics);
  const migratedFilesSizeInGb = calculateMigratedFilesSize(statistics, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics);
  const totalFileSizeInMb = calculateTotalFileSize(statistics, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics, true);
  const migratedFilesSizeInMb = calculateMigratedFilesSize(statistics, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics, true);

  let totalFinishedFiles =
    casesStatistics.caseFolderFilesMigrated +
    providersStatistics.filesMigrated +
    firmDocumentFoldersStatistics.filesMigrated +
    templateFilesStatistics.filesMigrated;
  if (displayOpenCaseAndIntakeStatistics) totalFinishedFiles += casesStatistics.openCaseFilesMigrated + intakesStatistics.openIntakeFilesMigrated;
  if (displayClosedCaseAndIntakeStatistics) totalFinishedFiles += casesStatistics.closedCaseFilesMigrated + intakesStatistics.closedIntakeFilesMigrated;
    
  let totalFailedInitialFiles =
    casesStatistics.caseFolderFilesInterruptedInitial +
    providersStatistics.filesInterruptedInitial +
    firmDocumentFoldersStatistics.filesInterruptedInitial +
    templateFilesStatistics.filesInterruptedInitial;
  if (displayOpenCaseAndIntakeStatistics) totalFailedInitialFiles += casesStatistics.openCaseFilesInterruptedInitial + intakesStatistics.openIntakeFilesInterruptedInitial;
  if (displayClosedCaseAndIntakeStatistics) totalFailedInitialFiles += casesStatistics.closedCaseFilesInterruptedInitial + intakesStatistics.closedIntakeFilesInterruptedInitial;
  
  let totalFailedDeltaFiles =
    casesStatistics.caseFolderFilesInterruptedDelta +
    providersStatistics.filesInterruptedDelta +
    firmDocumentFoldersStatistics.filesInterruptedDelta +
    templateFilesStatistics.filesInterruptedDelta;
  if (displayOpenCaseAndIntakeStatistics) totalFailedDeltaFiles += casesStatistics.openCaseFilesInterruptedDelta + intakesStatistics.openIntakeFilesInterruptedDelta;
  if (displayClosedCaseAndIntakeStatistics) totalFailedDeltaFiles += casesStatistics.closedCaseFilesInterruptedDelta + intakesStatistics.closedIntakeFilesInterruptedDelta;

  let totalModifiedFiles =
    casesStatistics.caseFolderFilesModified +
    providersStatistics.filesModified +
    firmDocumentFoldersStatistics.filesModified +
    templateFilesStatistics.filesModified;
  if (displayOpenCaseAndIntakeStatistics) totalModifiedFiles += casesStatistics.openCaseFilesModified + intakesStatistics.openIntakeFilesModified;
  if (displayClosedCaseAndIntakeStatistics) totalModifiedFiles += casesStatistics.closedCaseFilesModified + intakesStatistics.closedIntakeFilesModified;

  let totalNotFoundFiles =
    casesStatistics.caseFolderFilesNotFound +
    providersStatistics.filesNotFound +
    firmDocumentFoldersStatistics.filesNotFound +
    templateFilesStatistics.filesNotFound;
  if (displayOpenCaseAndIntakeStatistics) totalNotFoundFiles += casesStatistics.openCaseFilesNotFound + intakesStatistics.openIntakeFilesNotFound;
  if (displayClosedCaseAndIntakeStatistics) totalNotFoundFiles += casesStatistics.closedCaseFilesNotFound + intakesStatistics.closedIntakeFilesNotFound;

  let totalNotFoundMarkedForRetryFiles =
    casesStatistics.caseFolderFilesNotFoundMarkedForRetry +
    providersStatistics.filesNotFoundMarkedForRetry +
    firmDocumentFoldersStatistics.filesNotFoundMarkedForRetry +
    templateFilesStatistics.filesNotFoundMarkedForRetry;
  if (displayOpenCaseAndIntakeStatistics) totalNotFoundMarkedForRetryFiles += casesStatistics.openCaseFilesNotFoundMarkedForRetry + intakesStatistics.openIntakeFilesNotFoundMarkedForRetry;
  if (displayClosedCaseAndIntakeStatistics) totalNotFoundMarkedForRetryFiles += casesStatistics.closedCaseFilesNotFoundMarkedForRetry + intakesStatistics.closedIntakeFilesNotFoundMarkedForRetry;
  
  let totalFlaggedFiles = casesStatistics.caseFolderFilesFlagged;
  if (displayOpenCaseAndIntakeStatistics) totalFlaggedFiles += casesStatistics.openCaseFilesFlagged + intakesStatistics.openIntakeFilesFlagged;
  if (displayClosedCaseAndIntakeStatistics) totalFlaggedFiles += casesStatistics.closedCaseFilesFlagged + intakesStatistics.closedIntakeFilesFlagged;

  let totalSkippedDuplicateFiles = 
    casesStatistics.caseFolderFilesSkippedDuplicate
    + providersStatistics.filesSkippedDuplicate;
  if (displayOpenCaseAndIntakeStatistics) totalSkippedDuplicateFiles += casesStatistics.openCaseFilesSkippedDuplicate;
  if (displayClosedCaseAndIntakeStatistics) totalSkippedDuplicateFiles += casesStatistics.closedCaseFilesSkippedDuplicate;
  
  let totalNumberOfFiles =
    casesStatistics.totalNumberOfCaseFolderFiles +
    providersStatistics.totalNumberOfFiles +
    firmDocumentFoldersStatistics.totalNumberOfFiles +
    templateFilesStatistics.totalNumberOfFiles;
  if (displayOpenCaseAndIntakeStatistics) totalNumberOfFiles += casesStatistics.totalNumberOfOpenCaseFiles + intakesStatistics.totalNumberOfOpenIntakeFiles;
  if (displayClosedCaseAndIntakeStatistics) totalNumberOfFiles += casesStatistics.totalNumberOfClosedCaseFiles + intakesStatistics.totalNumberOfClosedIntakeFiles;
  
  let totalUnresolvedConflictFiles =
    casesStatistics.caseFilesInCaseFoldersUnresolvedConflictCount +
    providersStatistics.providerFilesUnresolvedConflictCount +
    firmDocumentFoldersStatistics.firmDocumentsUnresolvedConflictCount +
    templateFilesStatistics.templateFilesUnresolvedConflictCount;
  if (displayOpenCaseAndIntakeStatistics) totalUnresolvedConflictFiles += casesStatistics.caseFilesInOpenCasesUnresolvedConflictCount + intakesStatistics.intakeFilesInOpenIntakesUnresolvedConflictCount;
  if (displayClosedCaseAndIntakeStatistics) totalUnresolvedConflictFiles += casesStatistics.caseFilesInClosedCasesUnresolvedConflictCount + intakesStatistics.intakeFilesInClosedIntakesUnresolvedConflictCount;

  let totalAcknowledgedConflictFiles =
    casesStatistics.caseFilesInCaseFoldersAcknowledgedConflictCount +
    providersStatistics.providerFilesAcknowledgedConflictCount +
    firmDocumentFoldersStatistics.firmDocumentsAcknowledgedConflictCount +
    templateFilesStatistics.templateFilesAcknowledgedConflictCount;
  if (displayOpenCaseAndIntakeStatistics) totalAcknowledgedConflictFiles += casesStatistics.caseFilesInOpenCasesAcknowledgedConflictCount + intakesStatistics.intakeFilesInOpenIntakesAcknowledgedConflictCount;
  if (displayClosedCaseAndIntakeStatistics) totalAcknowledgedConflictFiles += casesStatistics.caseFilesInClosedCasesAcknowledgedConflictCount + intakesStatistics.intakeFilesInClosedIntakesAcknowledgedConflictCount;

  // Conflicted files are blocked from migration, so they must not be counted as "Not Started".
  const totalNotStartedFiles = Math.max(0,
    totalNumberOfFiles -
    totalFinishedFiles -
    totalFailedInitialFiles -
    totalFailedDeltaFiles -
    totalModifiedFiles -
    totalNotFoundFiles -
    totalNotFoundMarkedForRetryFiles -
    totalFlaggedFiles -
    totalSkippedDuplicateFiles -
    totalUnresolvedConflictFiles -
    totalAcknowledgedConflictFiles);

  // Not Found file sizes
  let notFoundFilesSizeInGb =
    (casesStatistics.notFoundCaseFolderFilesSizeInGb || 0) +
    (providersStatistics.notFoundProviderFilesSizeInGb || 0) +
    (firmDocumentFoldersStatistics.notFoundDocumentFilesSizeInGb || 0) +
    (templateFilesStatistics.notFoundTemplateFilesSizeInGb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    notFoundFilesSizeInGb += (casesStatistics.notFoundOpenCaseFilesSizeInGb || 0) + (intakesStatistics.notFoundOpenIntakeFilesSizeInGb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    notFoundFilesSizeInGb += (casesStatistics.notFoundClosedCaseFilesSizeInGb || 0) + (intakesStatistics.notFoundClosedIntakeFilesSizeInGb || 0);
  }

  let notFoundFilesSizeInMb =
    (casesStatistics.notFoundCaseFolderFilesSizeInMb || 0) +
    (providersStatistics.notFoundProviderFilesSizeInMb || 0) +
    (firmDocumentFoldersStatistics.notFoundDocumentFilesSizeInMb || 0) +
    (templateFilesStatistics.notFoundTemplateFilesSizeInMb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    notFoundFilesSizeInMb += (casesStatistics.notFoundOpenCaseFilesSizeInMb || 0) + (intakesStatistics.notFoundOpenIntakeFilesSizeInMb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    notFoundFilesSizeInMb += (casesStatistics.notFoundClosedCaseFilesSizeInMb || 0) + (intakesStatistics.notFoundClosedIntakeFilesSizeInMb || 0);
  }

  // Interrupted file sizes (initial + delta combined)
  let interruptedFilesSizeInGb =
    (casesStatistics.interruptedInitialCaseFolderFilesSizeInGb || 0) + (casesStatistics.interruptedDeltaCaseFolderFilesSizeInGb || 0) +
    (providersStatistics.interruptedInitialProviderFilesSizeInGb || 0) + (providersStatistics.interruptedDeltaProviderFilesSizeInGb || 0) +
    (firmDocumentFoldersStatistics.interruptedInitialDocumentFilesSizeInGb || 0) + (firmDocumentFoldersStatistics.interruptedDeltaDocumentFilesSizeInGb || 0) +
    (templateFilesStatistics.interruptedInitialTemplateFilesSizeInGb || 0) + (templateFilesStatistics.interruptedDeltaTemplateFilesSizeInGb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    interruptedFilesSizeInGb += (casesStatistics.interruptedInitialOpenCaseFilesSizeInGb || 0) + (casesStatistics.interruptedDeltaOpenCaseFilesSizeInGb || 0);
    interruptedFilesSizeInGb += (intakesStatistics.interruptedInitialOpenIntakeFilesSizeInGb || 0) + (intakesStatistics.interruptedDeltaOpenIntakeFilesSizeInGb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    interruptedFilesSizeInGb += (casesStatistics.interruptedInitialClosedCaseFilesSizeInGb || 0) + (casesStatistics.interruptedDeltaClosedCaseFilesSizeInGb || 0);
    interruptedFilesSizeInGb += (intakesStatistics.interruptedInitialClosedIntakeFilesSizeInGb || 0) + (intakesStatistics.interruptedDeltaClosedIntakeFilesSizeInGb || 0);
  }

  let interruptedFilesSizeInMb =
    (casesStatistics.interruptedInitialCaseFolderFilesSizeInMb || 0) + (casesStatistics.interruptedDeltaCaseFolderFilesSizeInMb || 0) +
    (providersStatistics.interruptedInitialProviderFilesSizeInMb || 0) + (providersStatistics.interruptedDeltaProviderFilesSizeInMb || 0) +
    (firmDocumentFoldersStatistics.interruptedInitialDocumentFilesSizeInMb || 0) + (firmDocumentFoldersStatistics.interruptedDeltaDocumentFilesSizeInMb || 0) +
    (templateFilesStatistics.interruptedInitialTemplateFilesSizeInMb || 0) + (templateFilesStatistics.interruptedDeltaTemplateFilesSizeInMb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    interruptedFilesSizeInMb += (casesStatistics.interruptedInitialOpenCaseFilesSizeInMb || 0) + (casesStatistics.interruptedDeltaOpenCaseFilesSizeInMb || 0);
    interruptedFilesSizeInMb += (intakesStatistics.interruptedInitialOpenIntakeFilesSizeInMb || 0) + (intakesStatistics.interruptedDeltaOpenIntakeFilesSizeInMb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    interruptedFilesSizeInMb += (casesStatistics.interruptedInitialClosedCaseFilesSizeInMb || 0) + (casesStatistics.interruptedDeltaClosedCaseFilesSizeInMb || 0);
    interruptedFilesSizeInMb += (intakesStatistics.interruptedInitialClosedIntakeFilesSizeInMb || 0) + (intakesStatistics.interruptedDeltaClosedIntakeFilesSizeInMb || 0);
  }

  // Skipped Duplicate file sizes (only cases + providers have this)
  let skippedDuplicateFilesSizeInGb =
    (casesStatistics.skippedDuplicateCaseFolderFilesSizeInGb || 0) +
    (providersStatistics.skippedDuplicateProviderFilesSizeInGb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    skippedDuplicateFilesSizeInGb += (casesStatistics.skippedDuplicateOpenCaseFilesSizeInGb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    skippedDuplicateFilesSizeInGb += (casesStatistics.skippedDuplicateClosedCaseFilesSizeInGb || 0);
  }

  let skippedDuplicateFilesSizeInMb =
    (casesStatistics.skippedDuplicateCaseFolderFilesSizeInMb || 0) +
    (providersStatistics.skippedDuplicateProviderFilesSizeInMb || 0);
  if (displayOpenCaseAndIntakeStatistics) {
    skippedDuplicateFilesSizeInMb += (casesStatistics.skippedDuplicateOpenCaseFilesSizeInMb || 0);
  }
  if (displayClosedCaseAndIntakeStatistics) {
    skippedDuplicateFilesSizeInMb += (casesStatistics.skippedDuplicateClosedCaseFilesSizeInMb || 0);
  }

  return {
    TotalFilesSizeInGb: totalFileSizeInGb,
    TotalFilesSizeInMb: totalFileSizeInMb,
    MigratedFilesSizeInGb: migratedFilesSizeInGb,
    MigratedFilesSizeInMb: migratedFilesSizeInMb,
    MigratedPercentage: (migratedFilesSizeInGb / totalFileSizeInGb) * 100,
    TotalFinishedFiles: totalFinishedFiles,
    TotalFailedInitialFiles: totalFailedInitialFiles,
    TotalFailedDeltaFiles: totalFailedDeltaFiles,
    TotalModifiedFiles: totalModifiedFiles,
    TotalNotFoundFiles: totalNotFoundFiles,
    TotalNotFoundMarkedForRetryFiles: totalNotFoundMarkedForRetryFiles,
    TotalNotStartedFiles: totalNotStartedFiles,
    TotalBlockedByConflictFiles: totalUnresolvedConflictFiles,
    TotalAcknowledgedConflictFiles: totalAcknowledgedConflictFiles,
    TotalFlaggedFiles: totalFlaggedFiles,
    TotalSkippedDuplicateFiles: totalSkippedDuplicateFiles,
    NotFoundFilesSizeInGb: notFoundFilesSizeInGb,
    NotFoundFilesSizeInMb: notFoundFilesSizeInMb,
    InterruptedFilesSizeInGb: interruptedFilesSizeInGb,
    InterruptedFilesSizeInMb: interruptedFilesSizeInMb,
    SkippedDuplicateFilesSizeInGb: skippedDuplicateFilesSizeInGb,
    SkippedDuplicateFilesSizeInMb: skippedDuplicateFilesSizeInMb,
  };
};
